import { type FormEvent, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { loginWithToken, type Session, signIn, signUpFirstAdmin, storedActor } from '@/lib/auth';
import { PASSWORD_MIN_LENGTH } from '@/lib/password';

/**
 * Centred card, gradient hairline, and one credential at a time (REDESIGN-2 §2).
 *
 * Three modes, because the hub accepts two credentials and has to be born
 * somehow:
 *
 * - **Sign in** is the front door: email and password against Better Auth.
 * - **First admin** is the bootstrap. The hub allows a public sign-up exactly
 *   while it has no accounts, so this is offered without asking whether it will
 *   work; the server is the only thing that knows, and it answers with a
 *   sentence when the answer is no.
 * - **Admin token** is the legacy path, kept because every script and the
 *   standing hub still use it. It is a link rather than a field, so nobody
 *   reaches for it by accident.
 *
 * The name field belongs to the token path alone. A signed-in person is named by
 * their account, and the hub ignores `x-dyspatch-actor` for a session.
 */
type Mode = 'sign-in' | 'first-admin' | 'token';

export function LoginGate({ onAuthenticated }: { onAuthenticated: (session: Session) => void }) {
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [token, setToken] = useState('');
  const [actor, setActor] = useState(storedActor() ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onAuthenticated(
        mode === 'token'
          ? await loginWithToken(token, actor)
          : mode === 'first-admin'
            ? await signUpFirstAdmin(name, email, password)
            : await signIn(email, password),
      );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'that did not work');
    } finally {
      setBusy(false);
    }
  }

  const link = 'text-fg-3 hover:text-foreground underline underline-offset-2 transition-colors';

  return (
    <div className="dy-floor flex min-h-screen items-center justify-center p-6">
      <div className="bg-card w-full max-w-[21rem] overflow-hidden rounded-xl border shadow-pop">
        <div className="h-[2px] w-full" style={{ background: 'var(--dy-grad)' }} />
        <form className="space-y-4 p-5" onSubmit={onSubmit}>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span
                className="size-[15px] shrink-0 rounded-[4px]"
                style={{ background: 'var(--dy-grad)' }}
                aria-hidden
              />
              <h1 className="dy-display dy-grad-text text-lg font-bold tracking-[-0.02em]">
                Dyspatch
              </h1>
            </div>
            <p className="text-fg-3 text-xs">
              {mode === 'first-admin'
                ? 'No account exists yet. The first one you make is the admin, and the password you type here is temporary: the console asks for a real one before it shows you anything.'
                : mode === 'token'
                  ? 'Sign in with the hub’s admin token.'
                  : 'A task hub for Nymbl’s Claude agents.'}
            </p>
          </div>

          {mode === 'first-admin' ? (
            <div className="space-y-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                value={name}
                autoComplete="name"
                placeholder="Sam Tripathi"
                onChange={(event) => setName(event.target.value)}
                required
                className="h-8"
              />
            </div>
          ) : null}

          {mode === 'token' ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="actor">Your name</Label>
                <Input
                  id="actor"
                  value={actor}
                  autoComplete="name"
                  placeholder="sam"
                  onChange={(event) => setActor(event.target.value)}
                  required
                  className="h-8"
                />
                <p className="text-fg-3 text-2xs">
                  Every action you take is recorded under this name.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="token">Admin token</Label>
                <Input
                  id="token"
                  type="password"
                  value={token}
                  autoComplete="off"
                  placeholder="admin token"
                  onChange={(event) => setToken(event.target.value)}
                  required
                  className="h-8"
                />
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  autoComplete="username"
                  placeholder="you@nymbl.app"
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  className="h-8"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  autoComplete={mode === 'first-admin' ? 'new-password' : 'current-password'}
                  placeholder="••••••••"
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                  className="h-8"
                />
              </div>
            </>
          )}

          {error ? (
            <p className="text-bad text-xs" role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="gate" className="w-full" disabled={busy}>
            {busy
              ? 'Working…'
              : mode === 'first-admin'
                ? 'Create the first admin'
                : mode === 'token'
                  ? 'Unlock'
                  : 'Sign in'}
          </Button>

          <div className="flex flex-wrap items-center justify-between gap-2 text-2xs">
            {mode === 'sign-in' ? (
              <button type="button" className={link} onClick={() => switchTo('first-admin')}>
                Set up the first admin
              </button>
            ) : (
              <button type="button" className={link} onClick={() => switchTo('sign-in')}>
                Back to sign in
              </button>
            )}
            {mode === 'token' ? null : (
              <button type="button" className={link} onClick={() => switchTo('token')}>
                Use an admin token
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
